type Restaurant = {
	streetNumber: string | null
	address: string | null
	route: string | null
	city: string | null
}

export function formatAddress(restaurant: Restaurant) {
	const streetNumber = restaurant.streetNumber
		? `${restaurant.streetNumber}, `
		: ""

	if (restaurant.route && restaurant.city) {
		return `${restaurant.route}, ${streetNumber}${restaurant.city}`
	}

	return restaurant.address
}

type CanonicalAddressArea = {
	city?: string | null
	region?: string | null
	postalCode?: string | null
	country?: string | null
}

function tokenizeAddress(value: string) {
	return value
		.toLowerCase()
		.split(/[\s,]+/)
		.filter(Boolean)
}

function peelSuffix(tokens: string[], end: number, value: string | null) {
	if (!value) {
		return end
	}
	const valueTokens = tokenizeAddress(value)
	if (valueTokens.length === 0 || valueTokens.length > end) {
		return end
	}
	const start = end - valueTokens.length
	const matches = valueTokens.every(
		(token, index) => tokens[start + index] === token,
	)
	return matches ? start : end
}

/**
 * Compose a canonical address string from a user-typed street line and the
 * Google-validated area fields, ordered `[street], [postalCode city], [region],
 * [country]`. An area value the user already appended to the street is
 * de-duplicated; the postal code is always kept.
 */
export function composeCanonicalAddress(
	streetLine: string | null | undefined,
	area: CanonicalAddressArea,
) {
	const street = (streetLine ?? "").trim()

	const city = area.city?.trim() || null
	const postalCode = area.postalCode?.trim() || null
	const region = area.region?.trim() || null
	const country = area.country?.trim() || null

	const streetTokens = tokenizeAddress(street)
	const afterCountry = peelSuffix(streetTokens, streetTokens.length, country)
	const afterRegion = peelSuffix(streetTokens, afterCountry, region)
	const afterCity = peelSuffix(streetTokens, afterRegion, city)

	const countryIsAppended = afterCountry < streetTokens.length
	const regionIsAppended = afterRegion < afterCountry
	const cityIsAppended = afterCity < afterRegion

	const locality =
		[postalCode, cityIsAppended ? null : city].filter(Boolean).join(" ") || null

	const segments = [
		street || null,
		locality,
		regionIsAppended ? null : region,
		countryIsAppended ? null : country,
	].filter(Boolean)

	return segments.join(", ")
}
